import { type ReferenceInfo, type WorkflowType } from '@blaze-shared/api-contracts-domain';

export interface WorkflowReferenceSection {
  title: string;
  references: ReferenceInfo[];
}

const SPECIAL_SECTIONS: { title: string; workflowTypes: WorkflowType[] }[] = [
  { title: 'SCHEDULED WORKFLOWS', workflowTypes: ['Scheduled'] },
  { title: 'LOGIN/SIGNUP WORKFLOWS', workflowTypes: ['Login', 'Signup'] },
  { title: 'TRIGGER WORKFLOWS', workflowTypes: ['Trigger'] },
  { title: 'WORKFLOW FUNCTIONS', workflowTypes: ['Function'] },
];

export function formatWorkflowReferenceLabel(reference: ReferenceInfo): string {
  return [reference.workflowName, reference.workflowId].filter(Boolean).join(' / ');
}

export function groupWorkflowReferences(references: ReferenceInfo[]): WorkflowReferenceSection[] {
  const sections: WorkflowReferenceSection[] = [];
  const regularReferences = references.filter((reference) => reference.workflowType === 'Regular');

  for (const pageName of uniqPageNames(regularReferences)) {
    const pageReferences = dedupeReferencesByWorkflowId(
      regularReferences.filter((reference) => reference.pageName === pageName),
    );

    if (pageReferences.length) {
      sections.push({
        title: `Page: ${pageName || 'Unknown'}`,
        references: pageReferences,
      });
    }
  }

  for (const { title, workflowTypes } of SPECIAL_SECTIONS) {
    const sectionReferences = dedupeReferencesByWorkflowId(
      references.filter((reference) => workflowTypes.includes(reference.workflowType)),
    );

    if (sectionReferences.length) {
      sections.push({ title, references: sectionReferences });
    }
  }

  const handledWorkflowTypes = new Set<WorkflowType>([
    'Regular',
    ...SPECIAL_SECTIONS.flatMap((section) => section.workflowTypes),
  ]);
  const remainingReferences = dedupeReferencesByWorkflowId(
    references.filter((reference) => !handledWorkflowTypes.has(reference.workflowType)),
  );

  if (remainingReferences.length) {
    sections.push({
      title: 'OTHER WORKFLOWS',
      references: remainingReferences,
    });
  }

  return sections;
}

function uniqPageNames(references: ReferenceInfo[]): string[] {
  return [...new Set(references.map((reference) => reference.pageName))];
}

function dedupeReferencesByWorkflowId(references: ReferenceInfo[]): ReferenceInfo[] {
  return [...new Map(references.map((reference) => [reference.workflowId, reference])).values()];
}
